import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
  FormLabel, RadioGroup, FormControlLabel, Radio,
  Divider, Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createProductCategory, fetchProductTypes } from "../../api/companyApi";
import {
  fetchMarketServices, fetchMarketGoods, fetchRoles,
  fetchIndustryGroups, fetchIndustriesByParent, fetchIndustryById,
  fetchGoalClassesByGoalType, fetchGoalTypesByGoalCategory, fetchGoalDetailsByGoalClass,
} from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

const HEALTH_GOAL_TYPE_ID = "goal_type_4";
const TRAINING_GOAL_CATEGORY_ID = "goal_category_1";

export default function ProductCategoryCreatePage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  // マスターデータ
  const [productTypes, setProductTypes] = useState([]);
  const [marketServices, setMarketServices] = useState([]);
  const [marketGoods, setMarketGoods] = useState([]);
  const [marketRoles, setMarketRoles] = useState([]);
  const [industryGroups, setIndustryGroups] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industrySubGroups, setIndustrySubGroups] = useState([]);
  const [goalClasses, setGoalClasses] = useState([]);
  const [goalTypes, setGoalTypes] = useState([]);
  const [goalDetails, setGoalDetails] = useState([]);

  const [form, setForm] = useState({
    productType: null,
    productCategoryName: "",
    productCategoryDescription: "",
    url: "",
    personality: "Person",
    service: null,
    serviceType: "",
    roles: [],
    goodses: [],
    selectedIndustryGroupId: "NONE",
    industryGroupId: "", industryGroupName: "",
    selectedIndustryId: "NONE",
    industryId: "", industryName: "",
    selectedIndustrySubGroupId: "NONE",
    industrySubGroupId: "", industrySubGroupName: "",
    goalClass: null,
    goalDetail: null,
    goalType: null,
  });

  useEffect(() => {
    const load = async (fn, setter) => {
      try { setter(Array.isArray(await fn()) ? await fn() : []); } catch { /* ignore */ }
    };
    (async () => {
      try { setProductTypes(Array.isArray(await fetchProductTypes(businessDomainId)) ? await fetchProductTypes(businessDomainId) : []); } catch { /* ignore */ }
    })();
    (async () => { try { setMarketServices(Array.isArray(await fetchMarketServices()) ? await fetchMarketServices() : []); } catch { /* ignore */ } })();
    (async () => { try { setMarketGoods(Array.isArray(await fetchMarketGoods()) ? await fetchMarketGoods() : []); } catch { /* ignore */ } })();
    (async () => { try { setMarketRoles(Array.isArray(await fetchRoles()) ? await fetchRoles() : []); } catch { /* ignore */ } })();
    (async () => { try { setIndustryGroups(Array.isArray(await fetchIndustryGroups()) ? await fetchIndustryGroups() : []); } catch { /* ignore */ } })();
  }, [businessDomainId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProductTypeChange = (e) => {
    const selected = productTypes.find((pt) => pt.productTypeId === e.target.value) || null;
    setForm({
      ...form,
      productType: selected,
      personality: "Person",
      service: null, serviceType: "",
      roles: [], goodses: [],
      selectedIndustryGroupId: "NONE", industryGroupId: "", industryGroupName: "",
      selectedIndustryId: "NONE", industryId: "", industryName: "",
      selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "",
      goalClass: null, goalDetail: null, goalType: null,
    });
    setIndustries([]); setIndustrySubGroups([]);
    setGoalClasses([]); setGoalTypes([]); setGoalDetails([]);
  };

  const handlePersonalityChange = (e) => {
    setForm({
      ...form,
      personality: e.target.value,
      roles: [],
      serviceType: "",
      selectedIndustryGroupId: "NONE", industryGroupId: "", industryGroupName: "",
      selectedIndustryId: "NONE", industryId: "", industryName: "",
      selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "",
      goalClass: null, goalDetail: null, goalType: null,
    });
    setIndustries([]); setIndustrySubGroups([]);
    setGoalClasses([]); setGoalTypes([]); setGoalDetails([]);
  };

  const handleServiceTypeChange = async (e) => {
    const val = e.target.value === form.serviceType ? "" : e.target.value;
    setForm({ ...form, serviceType: val, goalClass: null, goalDetail: null, goalType: null });
    setGoalClasses([]); setGoalTypes([]); setGoalDetails([]);
    try {
      if (val === "HealthService") {
        const classes = await fetchGoalClassesByGoalType(HEALTH_GOAL_TYPE_ID);
        setGoalClasses(Array.isArray(classes) ? classes : []);
      } else if (val === "TrainingService") {
        const types = await fetchGoalTypesByGoalCategory(TRAINING_GOAL_CATEGORY_ID);
        const filtered = (Array.isArray(types) ? types : []).filter(t => t.goalTypeName !== "BusinessPractice");
        setGoalTypes(filtered);
      }
    } catch { /* ignore */ }
  };

  const handleHealthGoalClass = async (e) => {
    const cls = e.target.value;
    setForm({ ...form, goalClass: cls, goalDetail: null });
    setGoalDetails([]);
    try {
      const details = await fetchGoalDetailsByGoalClass(cls.goalClassId);
      setGoalDetails(Array.isArray(details) ? details : []);
    } catch { /* ignore */ }
  };

  const handleTrainingGoalType = async (e) => {
    const gt = e.target.value;
    setForm({ ...form, goalType: gt, goalClass: null, goalDetail: null });
    setGoalClasses([]); setGoalDetails([]);
    try {
      const classes = await fetchGoalClassesByGoalType(gt.goalTypeId);
      setGoalClasses(Array.isArray(classes) ? classes : []);
    } catch { /* ignore */ }
  };

  const handleTrainingGoalClass = async (e) => {
    const cls = e.target.value;
    setForm({ ...form, goalClass: cls, goalDetail: null });
    setGoalDetails([]);
    try {
      const details = await fetchGoalDetailsByGoalClass(cls.goalClassId);
      setGoalDetails(Array.isArray(details) ? details : []);
    } catch { /* ignore */ }
  };

  const handleIndustryGroupChange = async (e) => {
    const id = e.target.value;
    setIndustries([]); setIndustrySubGroups([]);
    if (id === "NONE") {
      setForm({ ...form, selectedIndustryGroupId: "NONE", industryGroupId: "", industryGroupName: "", selectedIndustryId: "NONE", industryId: "", industryName: "", selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "" });
      return;
    }
    try {
      const grp = await fetchIndustryById(id);
      setForm({ ...form, selectedIndustryGroupId: id, industryGroupId: id, industryGroupName: grp.japaneseName || "", selectedIndustryId: "NONE", industryId: "", industryName: "", selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "" });
      const children = await fetchIndustriesByParent(id);
      setIndustries(Array.isArray(children) ? children : []);
    } catch { /* ignore */ }
  };

  const handleIndustryChange = async (e) => {
    const id = e.target.value;
    setIndustrySubGroups([]);
    if (id === "NONE") {
      setForm({ ...form, selectedIndustryId: "NONE", industryId: "", industryName: "", selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "" });
      return;
    }
    try {
      const ind = await fetchIndustryById(id);
      setForm({ ...form, selectedIndustryId: id, industryId: id, industryName: ind.japaneseName || "", selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "" });
      const children = await fetchIndustriesByParent(id);
      setIndustrySubGroups(Array.isArray(children) ? children : []);
    } catch { /* ignore */ }
  };

  const handleIndustrySubGroupChange = async (e) => {
    const id = e.target.value;
    if (id === "NONE") {
      setForm({ ...form, selectedIndustrySubGroupId: "NONE", industrySubGroupId: "", industrySubGroupName: "" });
      return;
    }
    try {
      const sub = await fetchIndustryById(id);
      setForm({ ...form, selectedIndustrySubGroupId: id, industrySubGroupId: id, industrySubGroupName: sub.japaneseName || "" });
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { productType, personality, service, roles, goodses } = form;
    if (!productType) return;

    const isService = productType.productClass !== "Goods";
    const isGoods = productType.productClass === "Goods";

    // バリデーション
    if (personality === "Person") {
      if (isGoods && goodses.length === 0) { notify.error(t("productCategory.error.goodsRequired")); return; }
      if (isService && !service) { notify.error(t("productCategory.error.serviceRequired")); return; }
    } else {
      if (isService) {
        if (roles.length === 0) { notify.error(t("productCategory.error.rolesRequired")); return; }
        if (!service) { notify.error(t("productCategory.error.serviceRequired")); return; }
      }
      if (isGoods && goodses.length === 0) { notify.error(t("productCategory.error.goodsRequired")); return; }
    }

    // ペイロード構築
    const payload = {
      companyId: COMPANY_ID,
      businessDomainId,
      businessUnitId,
      productCategoryName: form.productCategoryName,
      productCategoryDescription: form.productCategoryDescription || null,
      url: form.url || null,
      productTypeId: productType.productTypeId,
      productTypeName: productType.productTypeName,
      productClass: productType.productClass,
      personality,
      service: service ? { serviceId: String(service.id), serviceName: service.japaneseName } : null,
      serviceType: form.serviceType || null,
      roles: roles.map((r) => ({ roleId: String(r.id), roleName: r.japaneseName })),
      goodses: goodses.map((g) => ({ goodsId: String(g.id), goodsName: g.japaneseName })),
      industryGroupId: form.industryGroupId || "999",
      industryGroupName: form.industryGroupName || "",
      industryId: form.industryId || "999",
      industryName: form.industryName || "",
      industrySubGroupId: form.industrySubGroupId || "999",
      industrySubGroupName: form.industrySubGroupName || "",
    };

    // goal情報
    if (form.serviceType === "HealthService" && form.goalClass && form.goalDetail) {
      payload.goalCategory = "Health";
      payload.goalType = HEALTH_GOAL_TYPE_ID;
      payload.goalClass = form.goalClass.goalClassId;
      payload.goalDetail = form.goalDetail.goalDetailId;
    } else if (form.serviceType === "TrainingService" && form.goalType && form.goalClass && form.goalDetail) {
      payload.goalCategory = "Ability";
      payload.goalType = form.goalType.goalTypeId;
      payload.goalClass = form.goalClass.goalClassId;
      payload.goalDetail = form.goalDetail.goalDetailId;
    }

    try {
      await createProductCategory(payload);
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/product-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  const productClass = form.productType?.productClass;
  const isService = productClass === "ApplicationService" || productClass === "PersonalService";
  const isGoods = productClass === "Goods";

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("productCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* 製品タイプ選択 */}
          <Grid size={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>{t("productCategory.productType")}</InputLabel>
              <Select
                value={form.productType?.productTypeId || ""}
                onChange={handleProductTypeChange}
                label={t("productCategory.productType")}
              >
                {productTypes.map((pt) => (
                  <MenuItem key={pt.productTypeId} value={pt.productTypeId}>
                    {pt.productTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 製品タイプ選択後に展開 */}
          {form.productType && (
            <>
              {/* 製品カテゴリ名 */}
              <Grid size={12} md={6}>
                <TextField
                  fullWidth required
                  label={t("productCategory.name")}
                  value={form.productCategoryName}
                  onChange={(e) => setForm({ ...form, productCategoryName: e.target.value })}
                />
              </Grid>

              {/* 説明 */}
              <Grid size={12}>
                <TextField
                  fullWidth multiline rows={3}
                  label={t("productCategory.description")}
                  value={form.productCategoryDescription}
                  onChange={(e) => setForm({ ...form, productCategoryDescription: e.target.value })}
                />
              </Grid>

              {/* ── Service 系（ApplicationService / PersonalService） ── */}
              {isService && (
                <>
                  {/* サービス区分 */}
                  <Grid size={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{t("productCategory.service")}</InputLabel>
                      <Select
                        value={form.service?.id || ""}
                        onChange={(e) => {
                          const svc = marketServices.find((s) => s.id === e.target.value) || null;
                          setForm({ ...form, service: svc });
                        }}
                        label={t("productCategory.service")}
                      >
                        {marketServices.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.japaneseName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 個人/法人 */}
                  <Grid size={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">{t("productCategory.personality")}</FormLabel>
                      <RadioGroup row value={form.personality} onChange={handlePersonalityChange}>
                        <FormControlLabel value="Person" control={<Radio />} label={t("productCategory.personality.person")} />
                        <FormControlLabel value="Company" control={<Radio />} label={t("productCategory.personality.company")} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid size={12}><Divider /></Grid>

                  {/* 個人：自己実現支援サービス分類（任意） */}
                  {form.personality === "Person" && (
                    <>
                      <Grid size={12}>
                        <Alert severity="info" sx={{ mb: 1 }}>{t("productCategory.hint.serviceType")}</Alert>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("productCategory.serviceType")}</FormLabel>
                          <RadioGroup value={form.serviceType}>
                            <FormControlLabel value="HealthService" control={<Radio onClick={handleServiceTypeChange} />} label={t("productCategory.serviceType.health")} />
                            <FormControlLabel value="TrainingService" control={<Radio onClick={handleServiceTypeChange} />} label={t("productCategory.serviceType.training")} />
                            <FormControlLabel value="HouseworkSupportService" control={<Radio onClick={handleServiceTypeChange} />} label={t("productCategory.serviceType.housework")} />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* HealthService: goalClass → goalDetail */}
                      {form.serviceType === "HealthService" && (
                        <>
                          <Grid size={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel>{t("productCategory.goalClass")}</InputLabel>
                              <Select value={form.goalClass || ""} onChange={handleHealthGoalClass} label={t("productCategory.goalClass")}>
                                {goalClasses.map((c) => (
                                  <MenuItem key={c.goalClassId} value={c}>{c.goalClassNameJa || c.goalClassName}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {goalDetails.length > 0 && (
                            <Grid size={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t("productCategory.goalDetail")}</InputLabel>
                                <Select value={form.goalDetail || ""} onChange={(e) => setForm({ ...form, goalDetail: e.target.value })} label={t("productCategory.goalDetail")}>
                                  {goalDetails.map((d) => (
                                    <MenuItem key={d.goalDetailId} value={d}>{d.goalDetailNameJa || d.goalDetailName}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* TrainingService: goalType → goalClass → goalDetail */}
                      {form.serviceType === "TrainingService" && (
                        <>
                          <Grid size={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel>{t("productCategory.goalType")}</InputLabel>
                              <Select value={form.goalType || ""} onChange={handleTrainingGoalType} label={t("productCategory.goalType")}>
                                {goalTypes.map((gt) => (
                                  <MenuItem key={gt.goalTypeId} value={gt}>{gt.goalTypeNameJa || gt.goalTypeName}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {goalClasses.length > 0 && (
                            <Grid size={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t("productCategory.goalClass")}</InputLabel>
                                <Select value={form.goalClass || ""} onChange={handleTrainingGoalClass} label={t("productCategory.goalClass")}>
                                  {goalClasses.map((c) => (
                                    <MenuItem key={c.goalClassId} value={c}>{c.goalClassNameJa || c.goalClassName}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          {goalDetails.length > 0 && (
                            <Grid size={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t("productCategory.goalDetail")}</InputLabel>
                                <Select value={form.goalDetail || ""} onChange={(e) => setForm({ ...form, goalDetail: e.target.value })} label={t("productCategory.goalDetail")}>
                                  {goalDetails.map((d) => (
                                    <MenuItem key={d.goalDetailId} value={d}>{d.goalDetailNameJa || d.goalDetailName}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* 法人：役割 + 産業 */}
                  {form.personality === "Company" && (
                    <>
                      <Grid size={12}>
                        <Alert severity="info">{t("productCategory.hint.roles")}</Alert>
                      </Grid>
                      <Grid size={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>{t("productCategory.roles")}</InputLabel>
                          <Select
                            multiple
                            value={form.roles}
                            onChange={(e) => setForm({ ...form, roles: e.target.value })}
                            label={t("productCategory.roles")}
                            renderValue={(selected) => selected.map((r) => r.japaneseName).join(", ")}
                          >
                            {marketRoles.map((r) => (
                              <MenuItem key={r.id} value={r}>{r.japaneseName}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <IndustrySelectors
                        form={form}
                        industryGroups={industryGroups}
                        industries={industries}
                        industrySubGroups={industrySubGroups}
                        onGroupChange={handleIndustryGroupChange}
                        onIndustryChange={handleIndustryChange}
                        onSubGroupChange={handleIndustrySubGroupChange}
                        t={t}
                      />
                    </>
                  )}
                </>
              )}

              {/* ── Goods ── */}
              {isGoods && (
                <>
                  {/* 商品区分 */}
                  <Grid size={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{t("productCategory.goodses")}</InputLabel>
                      <Select
                        multiple
                        value={form.goodses}
                        onChange={(e) => setForm({ ...form, goodses: e.target.value })}
                        label={t("productCategory.goodses")}
                        renderValue={(selected) => selected.map((g) => g.japaneseName).join(", ")}
                      >
                        {marketGoods.map((g) => (
                          <MenuItem key={g.id} value={g}>{g.japaneseName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 個人/法人 */}
                  <Grid size={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">{t("productCategory.personality")}</FormLabel>
                      <RadioGroup row value={form.personality} onChange={handlePersonalityChange}>
                        <FormControlLabel value="Person" control={<Radio />} label={t("productCategory.personality.person")} />
                        <FormControlLabel value="Company" control={<Radio />} label={t("productCategory.personality.company")} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* 法人：産業 */}
                  {form.personality === "Company" && (
                    <>
                      <Grid size={12}><Divider /></Grid>
                      <IndustrySelectors
                        form={form}
                        industryGroups={industryGroups}
                        industries={industries}
                        industrySubGroups={industrySubGroups}
                        onGroupChange={handleIndustryGroupChange}
                        onIndustryChange={handleIndustryChange}
                        onSubGroupChange={handleIndustrySubGroupChange}
                        t={t}
                      />
                    </>
                  )}
                </>
              )}

              {/* URL */}
              <Grid size={12} md={6}>
                <TextField
                  fullWidth
                  label={t("common.url")}
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!form.productType}>{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/product-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}

// 産業セレクタ コンポーネント
function IndustrySelectors({ form, industryGroups, industries, industrySubGroups, onGroupChange, onIndustryChange, onSubGroupChange, t }) {
  return (
    <>
      <Grid size={12}>
        <Alert severity="info">{t("productCategory.hint.industry")}</Alert>
      </Grid>
      <Grid size={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>{t("productCategory.industryGroup")}</InputLabel>
          <Select value={form.selectedIndustryGroupId} onChange={onGroupChange} label={t("productCategory.industryGroup")}>
            <MenuItem value="NONE"><em>{t("common.none") || "なし"}</em></MenuItem>
            {industryGroups.map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.japaneseName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12} md={4}>
        <FormControl fullWidth disabled={industries.length === 0}>
          <InputLabel>{t("productCategory.industry")}</InputLabel>
          <Select value={form.selectedIndustryId} onChange={onIndustryChange} label={t("productCategory.industry")}>
            <MenuItem value="NONE"><em>{t("common.none") || "なし"}</em></MenuItem>
            {industries.map((ind) => (
              <MenuItem key={ind.id} value={ind.id}>{ind.japaneseName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12} md={4}>
        <FormControl fullWidth disabled={industrySubGroups.length === 0}>
          <InputLabel>{t("productCategory.industrySubGroup")}</InputLabel>
          <Select value={form.selectedIndustrySubGroupId} onChange={onSubGroupChange} label={t("productCategory.industrySubGroup")}>
            <MenuItem value="NONE"><em>{t("common.none") || "なし"}</em></MenuItem>
            {industrySubGroups.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.japaneseName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </>
  );
}
